import { Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { cn } from '@/lib/utils';

interface NFLStandingsTableProps {
  onNavigate?: (tab: string) => void;
}

export default function NFLStandingsTable({ onNavigate }: NFLStandingsTableProps) {
  const { teams, currentTeam } = useNFLLeague();

  const sorted = [...teams].sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    return b.points_for - a.points_for;
  });

  return (
    <Card
      className="bg-card border-border cursor-pointer hover:border-primary/30 transition-all"
      onClick={() => onNavigate?.('standings')}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-400" />
          Standings
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="h-10 w-10 rounded-full bg-amber-400/10 flex items-center justify-center mb-3">
              <Trophy className="h-5 w-5 text-amber-400/50" />
            </div>
            <p className="text-sm text-muted-foreground">Standings will appear here</p>
            <p className="text-xs text-muted-foreground mt-1">Once the season starts</p>
          </div>
        ) : (
          <div className="space-y-0">
            {/* Header */}
            <div className="flex items-center py-1.5 px-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
              <span className="w-6">#</span>
              <span className="flex-1">Team</span>
              <span className="w-14 text-center">Record</span>
              <span className="w-12 text-right">PF</span>
              <span className="w-12 text-right">PA</span>
            </div>

            {sorted.map((team, i) => (
              <div
                key={team.id}
                className={cn(
                  'flex items-center py-2 px-2 rounded-md text-sm',
                  team.id === currentTeam?.id && 'bg-primary/5 border border-primary/10'
                )}
              >
                <span className="w-6 text-xs text-muted-foreground">{i + 1}</span>
                <span className={cn(
                  'flex-1 truncate font-medium',
                  team.id === currentTeam?.id ? 'text-primary' : 'text-foreground'
                )}>
                  {team.name}
                </span>
                <span className="w-14 text-center text-xs text-muted-foreground">
                  {team.wins}-{team.losses}{team.ties ? `-${team.ties}` : ''}
                </span>
                <span className="w-12 text-right text-xs text-muted-foreground">
                  {Math.round(team.points_for)}
                </span>
                <span className="w-12 text-right text-xs text-muted-foreground">
                  {Math.round(team.points_against)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
